import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Drawer,
  IconButton,
  Tooltip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  LogoutOutlined,
  MenuOutlined,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getTenantId } from "../../utils/jwtUtils";
import { useActiveWorkspace } from "../../hooks/useActiveWorkspace";
import {
  ALWAYS_VISIBLE_NAV,
  SUPPORT_NAV,
  filterNavEntries,
  navPathMatches,
  type NavGroup,
  type NavIconComponent,
  type NavLeaf,
} from "../../utils/workspaces";
import ThemeModeToggle from "./ThemeModeToggle";
import OrgSwitcher from "./OrgSwitcher";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import JourneyCompanionBar from "../home/JourneyCompanionBar";

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

/** Outlined icons read cleaner in dense nav; 22px matches modern app sidebars. */
const NAV_ICON_SX = { fontSize: 22 } as const;

function NavIcon({ icon: Icon }: { icon: NavIconComponent }) {
  return <Icon sx={NAV_ICON_SX} />;
}

const isGroupActive = (group: NavGroup, pathname: string): boolean =>
  group.children.some((child) =>
    navPathMatches(pathname, child.path, child.exact),
  );

const logoSx = {
  margin: 0,
  letterSpacing: "-0.03em",
  fontWeight: 500,
  background: "linear-gradient(74deg, #34a853, #ea4335, #fbbc04, #4285f4)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  lineHeight: 1.2,
} as const;

interface MainNavigationProps {
  children: React.ReactNode;
}

const MainNavigation: React.FC<MainNavigationProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, logout, activeTenantId } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const {
    available,
    active: activeWorkspace,
    navItems: workspaceNavItems,
    selectWorkspace,
    accessCtx,
  } = useActiveWorkspace();

  const navItems = useMemo(() => {
    const home = filterNavEntries(ALWAYS_VISIBLE_NAV, accessCtx);
    const support = token ? filterNavEntries([SUPPORT_NAV], accessCtx) : [];
    return [...home, ...workspaceNavItems, ...support];
  }, [workspaceNavItems, accessCtx, token]);

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [groupMenu, setGroupMenu] = useState<{
    label: string;
    anchor: HTMLElement;
  } | null>(null);

  const effectiveCollapsed = isDesktop && collapsed;
  const drawerWidth = effectiveCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const isActive = (path: string, exact?: boolean) =>
    navPathMatches(location.pathname, path, exact);

  useEffect(() => {
    navItems.forEach((entry) => {
      if (entry.kind === "group" && isGroupActive(entry, location.pathname)) {
        setOpenGroups((prev) =>
          prev[entry.label] ? prev : { ...prev, [entry.label]: true },
        );
      }
    });
  }, [location.pathname, navItems]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    navigate("/");
  };

  const userLabel = user?.name || user?.email || "Account";
  const tenantLabel =
    activeTenantId ?? user?.tenantId ?? getTenantId() ?? null;

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      {isDesktop && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: effectiveCollapsed ? "center" : "flex-end",
            px: effectiveCollapsed ? 0 : 2,
            py: 1,
            minHeight: 48,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setCollapsed(!effectiveCollapsed)}
            aria-label={effectiveCollapsed ? "Expand menu" : "Collapse menu"}
          >
            {effectiveCollapsed ? (
              <ChevronRightOutlined sx={NAV_ICON_SX} />
            ) : (
              <ChevronLeftOutlined sx={NAV_ICON_SX} />
            )}
          </IconButton>
        </Box>
      )}

      {token && <OrgSwitcher collapsed={effectiveCollapsed} />}
      {token && (
        <WorkspaceSwitcher
          available={available}
          active={activeWorkspace}
          onSelect={selectWorkspace}
          collapsed={effectiveCollapsed}
        />
      )}

      <Divider />

      <List sx={{ flexGrow: 1, px: effectiveCollapsed ? 0.5 : 1, py: 1 }}>
        {navItems.map((item) => {
          const listItemSx = {
            borderRadius: 1,
            mb: 0.5,
            justifyContent: effectiveCollapsed ? "center" : "flex-start",
            px: effectiveCollapsed ? 1 : 2,
            "&.Mui-selected": {
              bgcolor: "action.selected",
              borderLeft: effectiveCollapsed ? "none" : "3px solid",
              borderColor: "grey.700",
              "&:hover": { bgcolor: "action.selected" },
            },
          } as const;

          const renderLeaf = (leaf: Omit<NavLeaf, "kind">, nested = false) => {
            const active = isActive(leaf.path, leaf.exact);
            const button = (
              <ListItemButton
                key={leaf.label}
                selected={active}
                onClick={() => handleNavigate(leaf.path)}
                sx={{
                  ...listItemSx,
                  pl: nested && !effectiveCollapsed ? 4 : listItemSx.px,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: effectiveCollapsed ? 0 : nested ? 36 : 40,
                    justifyContent: "center",
                    color: active ? "text.primary" : "text.secondary",
                  }}
                >
                  <NavIcon icon={leaf.icon} />
                </ListItemIcon>
                {!effectiveCollapsed && (
                  <ListItemText
                    primary={leaf.label}
                    primaryTypographyProps={{
                      fontSize: nested ? "0.875rem" : "0.9375rem",
                      fontWeight: active ? 600 : 400,
                      color: active ? "text.primary" : "text.secondary",
                    }}
                  />
                )}
              </ListItemButton>
            );

            return effectiveCollapsed ? (
              <Tooltip key={leaf.label} title={leaf.label} placement="right">
                {button}
              </Tooltip>
            ) : (
              button
            );
          };

          if (item.kind === "leaf") {
            return renderLeaf(item);
          }

          const groupActive = isGroupActive(item, location.pathname);
          const groupOpen = openGroups[item.label] ?? false;
          const groupButton = (
            <ListItemButton
              selected={groupActive}
              onClick={(event) => {
                if (effectiveCollapsed) {
                  setGroupMenu({
                    label: item.label,
                    anchor: event.currentTarget,
                  });
                  return;
                }
                setOpenGroups((prev) => ({
                  ...prev,
                  [item.label]: !groupOpen,
                }));
              }}
              sx={listItemSx}
            >
              <ListItemIcon
                sx={{
                  minWidth: effectiveCollapsed ? 0 : 40,
                  justifyContent: "center",
                  color: groupActive ? "text.primary" : "text.secondary",
                }}
              >
                <NavIcon icon={item.icon} />
              </ListItemIcon>
              {!effectiveCollapsed && (
                <>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "0.9375rem",
                      fontWeight: groupActive ? 600 : 400,
                      color: groupActive ? "text.primary" : "text.secondary",
                    }}
                  />
                  {groupOpen ? (
                    <ExpandLessOutlined sx={{ fontSize: 20, color: "text.secondary" }} />
                  ) : (
                    <ExpandMoreOutlined sx={{ fontSize: 20, color: "text.secondary" }} />
                  )}
                </>
              )}
            </ListItemButton>
          );

          return (
            <React.Fragment key={item.label}>
              {effectiveCollapsed ? (
                <Tooltip title={item.label} placement="right">
                  {groupButton}
                </Tooltip>
              ) : (
                groupButton
              )}
              {!effectiveCollapsed && (
                <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.children.map((child) => renderLeaf(child, true))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      <Menu
        anchorEl={groupMenu?.anchor ?? null}
        open={Boolean(groupMenu)}
        onClose={() => setGroupMenu(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {navItems
          .filter(
            (entry): entry is NavGroup =>
              entry.kind === "group" && entry.label === groupMenu?.label,
          )
          .flatMap((group) => group.children)
          .map((child) => (
            <MenuItem
              key={child.path}
              selected={isActive(child.path, child.exact)}
              onClick={() => {
                handleNavigate(child.path);
                setGroupMenu(null);
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <NavIcon icon={child.icon} />
              </ListItemIcon>
              <ListItemText primary={child.label} />
            </MenuItem>
          ))}
      </Menu>

      <Divider />

      {token && (
        <List sx={{ px: effectiveCollapsed ? 0.5 : 1, py: 0.5 }}>
          {!effectiveCollapsed && (
            <Box sx={{ px: 2, py: 1 }}>
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontSize: "0.75rem",
                  color: "text.secondary",
                }}
              >
                Signed in as
              </Box>
              <ListItemButton
                onClick={() => navigate("/profile")}
                sx={{
                  px: 0,
                  py: 0.25,
                  minHeight: 0,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {userLabel}
                </Box>
              </ListItemButton>
              {tenantLabel && (
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    mt: 0.25,
                  }}
                >
                  {tenantLabel}
                </Box>
              )}
            </Box>
          )}
          <ThemeModeToggle variant="toggle" collapsed={effectiveCollapsed} />
          {(() => {
            const logoutButton = (
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 1,
                  justifyContent: effectiveCollapsed ? "center" : "flex-start",
                  px: effectiveCollapsed ? 1 : 2,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: effectiveCollapsed ? 0 : 40,
                    justifyContent: "center",
                    color: "text.secondary",
                  }}
                >
                  <LogoutOutlined sx={NAV_ICON_SX} />
                </ListItemIcon>
                {!effectiveCollapsed && (
                  <ListItemText
                    primary="Log out"
                    primaryTypographyProps={{
                      fontSize: "0.9375rem",
                      color: "text.secondary",
                    }}
                  />
                )}
              </ListItemButton>
            );
            return effectiveCollapsed ? (
              <Tooltip title="Log out" placement="right">
                {logoutButton}
              </Tooltip>
            ) : (
              logoutButton
            );
          })()}
        </List>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: "block", md: "none" },
          bgcolor: "background.default",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <MenuOutlined sx={NAV_ICON_SX} />
          </IconButton>
          <Box
            component="h1"
            onClick={() => handleNavigate("/")}
            sx={{ ...logoSx, fontSize: "1.75rem", cursor: "pointer" }}
          >
            SAMi
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {token && (
              <WorkspaceSwitcher
                available={available}
                active={activeWorkspace}
                onSelect={selectWorkspace}
                variant="compact"
              />
            )}
            {token && <OrgSwitcher variant="compact" />}
          </Box>
          <ThemeModeToggle />
          {token && (
            <Tooltip title="Log out">
              <IconButton onClick={handleLogout} aria-label="Log out">
                <LogoutOutlined sx={NAV_ICON_SX} />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            bgcolor: "background.default",
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: "background.default",
            borderRight: "1px solid",
            borderColor: "divider",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: "64px", md: 0 },
        }}
      >
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {children}
        </Box>
        <JourneyCompanionBar />
      </Box>
    </Box>
  );
};

export default MainNavigation;
